import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { NgEntityService } from '@datorama/akita-ng-entity-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Point } from 'geojson';

import { CameraCommand, CAMERA_COMMAND_NAME, CAMERA_IMAGE_MODE, CAMERA_RECORDING, UAVCommand, UAV_COMMAND_NAME } from '../../model/uavcommand';
import { UpdateActionType } from '../../service/generic-akita-http.service';
import { WebsocketService } from '../../service/websocket.service';
import { UAVCommandState, UAVCommandStore } from '../state/uavcommand.store';
import { UAVCommandHTTPService } from './uav-command-http.service';
import { UAVStatus } from '../../model/UAVStatus';
import { UrlConfig } from '../../model/urlconfig';

const options = {
  params: {
    options: 'keyValues'
  }
};

export const DEFAULT_CAMERA_ALTITUDE = 0;
export const DEFAULT_CAMERA_DISTANCE = 10;
export const DEFAULT_FLIGHT_ALTITUDE = 70;
export const MIN_FLIGHT_ALTITUDE = 5;
export const MAX_FLIGHT_ALTITUDE = 100;


@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UAVCommandService extends NgEntityService<UAVCommandState> implements UrlConfig {
  constructor(
    protected store: UAVCommandStore,
    private uavCommandHTTPService: UAVCommandHTTPService,
    private webSocketService: WebsocketService,
  ) {
    super(store);

    this.webSocketService.getDataStream('UAVCommand')
      .pipe(untilDestroyed(this))
      .subscribe((data: UAVCommand[]) => this.store.upsertMany(data));
  }
  updateEntitiesUrl(newUrl: string): void {
    this.uavCommandHTTPService.setEntitiesUrl(newUrl);
  }

  getAll() {
    this.uavCommandHTTPService.getAll().subscribe(uavs => this.store.set(uavs));
  }

  getById(id: ID) {
    this.uavCommandHTTPService.loadById(id).subscribe(uavs => this.store.add(uavs));
  }

  updateUAVCommand(cmd: UAVCommand, poi: Point, drone: UAVStatus, name?: UAV_COMMAND_NAME, cameraCommand?: CameraCommand) {
    const poiWithAltitude = { ...poi };
    if (poiWithAltitude.coordinates[2] == null) {
      poiWithAltitude.coordinates[2] = drone?.location?.coordinates[2] ?? DEFAULT_FLIGHT_ALTITUDE;
    }

    const command: Partial<UAVCommand> = {
      id: cmd.id,
      type: 'UAVCommand',
      command: {
        uavPosition: poiWithAltitude,
        commandName: name ?? UAV_COMMAND_NAME.FLY_TO_POI
      }
    };
    if(cameraCommand){
      command.cameraCommand = cameraCommand;
    }

    this.store.update(cmd.id, command);
    this.uavCommandHTTPService.update(command, UpdateActionType.APPEND, options).subscribe(
      data => console.log('Updated UAVCommand', data ?? command),
      error => console.log('Error inserting in Hub', error)
    );
  }


  updateCameraCommand(
    cmd: UAVCommand,
    cameraPoint: Point,
    imageMode: CAMERA_IMAGE_MODE,
    cameraCommandName: CAMERA_COMMAND_NAME,
    zoomLevel: number,
    cameraRecording: CAMERA_RECORDING,
    doSnapshot: boolean
  ) {
    const cloned: UAVCommand = {
      id: cmd.id,
      type: 'UAVCommand',
      cameraCommand: {
        cameraPosition: cameraPoint,
        cameraCommandName,
        cameraImageMode: imageMode,
        zoomLevel
      },
      cameraRecording,
      doSnapshot
    };
    this.store.update(cmd.id, entity => entity = cloned);

    this.uavCommandHTTPService.update(cloned, UpdateActionType.APPEND, options).subscribe(
      data => console.log('Updated UAVCMD', data),
      error => console.log('Error inserting in Hub', error)
    );
  }



  createNewCommand(uavCmdId: string, poi: Point, drone: UAVStatus, name?: UAV_COMMAND_NAME, cameraCommand?: CameraCommand) {
    const poiWithAltitude = { ...poi };
    poiWithAltitude.coordinates[2] = drone?.location?.coordinates[2] ?? DEFAULT_FLIGHT_ALTITUDE;


    const command: UAVCommand = {
      id: uavCmdId,
      type: 'UAVCommand',
      command: {
        uavPosition: poiWithAltitude,
        commandName: name ?? UAV_COMMAND_NAME.FLY_TO_POI
      },
      cameraCommand: {
        cameraCommandName: cameraCommand?.cameraCommandName ?? CAMERA_COMMAND_NAME.STOW,
        cameraImageMode: cameraCommand?.cameraImageMode ?? CAMERA_IMAGE_MODE.RGB,
        cameraPosition: cameraCommand?.cameraPosition ?? poi, // TODO: Default camera position should be redefined, e.g., as a STOW point or undefined.
        zoomLevel: 0
      },
      cameraRecording: CAMERA_RECORDING.OFF
    };

    this.store.add(command);

    this.uavCommandHTTPService.update(command, UpdateActionType.APPEND, options).subscribe(
      data => console.log('Updated UAVCMD', data),
      error => console.log('Error inserting in Hub', error)
    );
  }


}
